import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { State } from '../../../../core/store';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { loadJobTitles } from '../../../../core/store/profile/profile.actions';
import { EmployeeReviewTemplateService } from '../../../../core/services/employee-review-template.service';
import { KraMetricCriterion, KraMetricVersion, MetricDefinition, KraMetricConfiguration, KraMetricResult, metricResultStatuses, metricScoreStatuses, metricRatingStatuses } from '../../../../models/kraMetricModels';
import { AlertService } from '../../../../shared/services/alert.service';

@Component({ selector: 'app-kra-metrics', templateUrl: './kra-metrics.component.html', standalone: false })
export class KraMetricsComponent extends BaseComponent implements OnInit {
  templateID: number;
  templateName = '';
  loading = true;
  saving = false;
  confirmingPublish = false;
  error = '';
  search = '';
  criterionSearch = '';
  criteria: KraMetricCriterion[] = [];
  library: MetricDefinition[] = [];
  selected: KraMetricCriterion;
  draft: KraMetricVersion;
  original = '';
  jobTitles: { label: string; value: number }[] = [];
  assignedJobTitleIDs: number[] = [];
  evaluationMethods = ['Manager rating', 'Use metrics', 'Metrics and manager rating', 'Enter results by hand', 'Information only (no score)'];
  methodHelp = ['The manager gives a rating. A metric is optional.',
    'Use measured results. Set up the data before using a metric in the score.',
    'Combine measured results with a manager rating. Set each part\'s share of the score under Scoring settings.',
    'Enter the data yourself, then calculate the results.', 'Show results without giving this criterion a score.'];
  operators = ['Above (>)', 'At least (>=)', 'Below (<)', 'At most (<=)', 'Exactly (=)', 'Between'];
  windows = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'KRA review period', 'Rolling X days'];
  readiness = ['Ready', 'Setup needed', 'More tracking needed', 'Outside data needed', 'Enter by hand', 'Needs review'];
  neutralPolicies = ['Use the other scored parts', 'Leave the score incomplete', 'Leave this criterion out of the period score', 'Ask someone to enter the missing result'];
  statuses = metricResultStatuses;
  scoreStatuses = metricScoreStatuses;
  ratingStatuses = metricRatingStatuses;
  conversions = ['Goal met / not met', 'Percentage ranges', 'Goal threshold', 'Multiply and adjust the result', 'Custom ranges'];
  addedMetricName = '';
  analysisJSON = '';
  employees: {id: number; name: string}[] = [];
  employeeID: number;
  resultVersionID: number;
  periodStart = new Date().toISOString().slice(0, 10);
  periodEnd = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  managerRating: number;
  result: KraMetricResult;
  capture: any = {};
  captureMetricID: number;
  timeZones: {id: string; name: string}[] = [];
  periodTimeZoneID: string;
  captureRevision: number;
  captureRevisionKey = '';
  correctionReason = '';
  requestFingerprint = '';
  requestID = '';


  constructor(private route: ActivatedRoute, private service: EmployeeReviewTemplateService,
    private alerts: AlertService, private store: Store<State>) { super(); }

  ngOnInit() {
    this.templateID = Number(this.route.snapshot.params.templateid);
    this.store.dispatch(loadJobTitles());
    this.subs.push(this.store.select(s => s.profileState.jobTitles).subscribe(rows => {
      this.jobTitles = (rows || []).map(x => ({ label: x.jobTitle, value: x.id }));
    }));
    this.load();
  }

  load() {
    this.loading = true;
    this.error = '';
    this.subs.push(forkJoin({ library: this.service.getMetricLibrary(), criteria: this.service.getMetricCriteria(this.templateID),
      template: this.service.getTemplate(this.templateID), employees: this.service.getMetricEmployees(this.templateID), timeZones: this.service.getMetricTimeZones() }).subscribe({
      next: result => {
        this.employees = result.employees;
        this.timeZones = result.timeZones;
        this.library = result.library;
        this.criteria = result.criteria;
        this.templateName = result.template.description;
        this.assignedJobTitleIDs = result.template.jobTitleIDs || [];
        this.loading = false;
      }, error: err => { this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; this.loading = false; }
    }));
  }

  get filteredCriteria() {
    const query = this.criterionSearch.toLocaleLowerCase();
    return this.criteria.filter(x => `${x.kra} ${x.wording} ${x.context}`.toLocaleLowerCase().includes(query));
  }

  get filteredLibrary() {
    const query = this.search.toLocaleLowerCase();
    return this.library.filter(x => x.active && !this.library.some(y => y.id === x.id && y.version > x.version))
      .filter(x => `${x.name} ${x.category} ${x.requiredInputs}`.toLocaleLowerCase().includes(query));
  }

  get resultVersion() { return this.selected?.history.find(x => x.id === this.resultVersionID); }
  get captureSource() { return this.resultVersion?.metrics.find(x => x.metricDefinitionID === this.captureMetricID); }
  get captureKey() { return JSON.stringify([this.employeeID, this.resultVersionID, this.captureMetricID, this.capture.sourceRecordID]); }

  get applicableJobTitles() { return this.jobTitles.filter(x => this.assignedJobTitleIDs.includes(x.value)); }

  get hasUnsavedChanges() { return !!this.draft && !this.selected?.retired && JSON.stringify(this.draft) !== this.original; }

  canLeave(): boolean | Observable<boolean> {
    if (this.saving) { return false; }
    return this.hasUnsavedChanges
      ? this.alerts.confirm('Unsaved settings', 'Leave this page and discard your changes?').pipe(take(1)) : true;
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent) {
    if (this.saving || this.hasUnsavedChanges) { event.preventDefault(); event.returnValue = ''; }
  }

  choose(criterion: KraMetricCriterion) {
    if (this.saving) { return; }
    if (this.hasUnsavedChanges) {
      this.subs.push(this.alerts.confirm('Unsaved settings', 'Discard your changes and choose another criterion?')
        .pipe(take(1)).subscribe(confirmed => { if (confirmed) { this.select(criterion); } }));
    } else { this.select(criterion); }
  }

  select(criterion: KraMetricCriterion) {
    this.selected = criterion;
    this.addedMetricName = '';
    this.resultVersionID = criterion.configuration.id;
    this.result = undefined;
    this.managerRating = undefined;
    this.captureRevisionKey = ''; this.captureRevision = undefined; this.correctionReason = '';
    this.draft = JSON.parse(JSON.stringify(criterion.configuration));
    if (criterion.retired) {
      this.draft.effectiveStart = this.draft.effectiveStart.slice(0, 10);
      this.draft.effectiveEnd = this.draft.effectiveEnd?.slice(0, 10);
      this.original = JSON.stringify(this.draft);
      this.error = '';
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const previous = new Date(`${this.draft.effectiveStart.slice(0, 10)}T00:00:00Z`);
    previous.setUTCDate(previous.getUTCDate() + 1);
    const successor = previous.toISOString().slice(0, 10);
    this.draft.effectiveStart = this.draft.id && successor > today ? successor : today;
    this.draft.effectiveEnd = undefined;
    this.draft.approved = false;
    this.draft.metrics.forEach(metric => metric.activated = false);
    this.original = JSON.stringify(this.draft);
    this.error = '';
  }

  add(definition: MetricDefinition) {
    if (!this.draft || this.saving || this.draft.metrics.some(x => x.metricDefinitionID === definition.id)) { return; }
    this.draft.metrics.push({ metricDefinitionID: definition.id, metricDefinitionVersionID: definition.versionID,
      targetOperator: null, measurementWindow: null, readiness: definition.readiness,
      readinessReason: definition.readinessReason, affectsScore: false, displayOnly: true });
    this.addedMetricName = definition.name;
  }

  isConnected(definitionID: number) { return this.draft?.metrics.some(x => x.metricDefinitionID === definitionID); }

  definition(versionID: number) { return this.library.find(x => x.versionID === versionID); }

  migrate() {
    if (this.saving || this.hasUnsavedChanges) { this.error = 'Save or discard your changes before finding suggested metrics.'; return; }
    this.saving = true;
    this.subs.push(this.service.migrateMetricCandidates(this.templateID).subscribe({ next: report => {
      this.saving = false; this.selected = undefined; this.draft = undefined; this.load(); this.alerts.success(report.message);
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; } }));
  }

  prefill() {
    if (this.saving || this.hasUnsavedChanges) { this.error = 'Save or discard your changes before adding available data sources.'; return; }
    this.saving = true;
    this.subs.push(this.service.prefillMetricSources(this.templateID).subscribe({ next: report => {
      this.saving = false; this.selected = undefined; this.draft = undefined; this.load(); this.alerts.success(report.message);
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; } }));
  }

  importAnalysis() {
    if (this.saving || this.hasUnsavedChanges) { this.error = 'Save or discard your changes before importing.'; return; }
    let records: KraMetricCriterion[];
    try { records = JSON.parse(this.analysisJSON); } catch { this.error = 'The import file is not valid JSON. Ask your setup team to check it, then try again.'; return; }
    this.saving = true;
    this.subs.push(this.service.importMetricAnalysis(records).subscribe({ next: () => {
      this.saving = false; this.selected = undefined; this.draft = undefined; this.load(); this.alerts.success('Suggestions imported as drafts. Review them before use.');
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : JSON.stringify(err.error) || err.message; } }));
  }

  setConversion(metric: KraMetricConfiguration, value: string) {
    metric.ratingConversion = value === '' ? undefined : { type: Number(value), bands: [] };
  }

  calculate() {
    if (this.saving || !this.resultVersionID || !this.employeeID) { return; }
    const settings = { criterionVersionID: this.resultVersionID, userID: this.employeeID, periodStart: this.periodStart,
      periodEnd: this.periodEnd, periodTimeZoneID: this.periodTimeZoneID,
      managerRating: this.resultVersion?.evaluationMethod === 0 || this.resultVersion?.evaluationMethod === 2 ? this.managerRating : undefined };
    const fingerprint = JSON.stringify(settings);
    if (fingerprint !== this.requestFingerprint) { this.requestFingerprint = fingerprint; this.requestID = crypto.randomUUID(); }
    this.saving = true; this.error = '';
    this.subs.push(this.service.calculateMetric({ ...settings, requestID: this.requestID }).subscribe({ next: result => {
      this.result = result; this.saving = false; this.requestFingerprint = '';
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; } }));
  }

  captureOpportunity() {
    if (this.saving || !this.employeeID || !this.resultVersionID || !this.captureMetricID) { return; }
    const submitted = JSON.parse(JSON.stringify({ criterionVersionID: this.resultVersionID, userID: this.employeeID,
      metricDefinitionID: this.captureMetricID, opportunity: this.capture,
      expectedRevision: this.captureRevisionKey === this.captureKey ? this.captureRevision : 0, correctionReason: this.correctionReason }));
    const key = this.captureKey;
    this.saving = true; this.error = '';
    this.subs.push(this.service.captureMetricOpportunity(submitted).subscribe({ next: record => {
      this.captureRevision = record.revision; this.captureRevisionKey = key; this.correctionReason = '';
      this.saving = false; this.alerts.success('Data saved. Past results stay the same. Calculate again to use this data in a new result.');
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; } }));
  }

  findOpportunity() {
    if (this.saving || !this.employeeID || !this.resultVersionID || !this.captureMetricID || !this.capture.sourceRecordID) { return; }
    const key = this.captureKey;
    const submitted = { criterionVersionID: this.resultVersionID, userID: this.employeeID,
      metricDefinitionID: this.captureMetricID, opportunity: { sourceRecordID: this.capture.sourceRecordID } };
    this.saving = true; this.error = '';
    this.subs.push(this.service.findMetricOpportunity(submitted).subscribe({ next: record => {
      this.capture = record.opportunity;
      this.capture.opportunityDate = this.capture.opportunityDate.slice(0, 19);
      for (const field of ['startTime', 'dueTime', 'completionTime']) { this.capture[field] = this.capture[field]?.slice(0, 19); }
      this.captureRevision = record.revision; this.captureRevisionKey = key; this.correctionReason = ''; this.saving = false;
      if (!record.timestampsUtc) { this.error = 'This record has no confirmed time zone. Check the dates and times, then save a correction with a reason.'; }
    }, error: err => { this.saving = false; this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message; } }));
  }

  publish() {
    if (this.saving || !this.selected || this.selected.retired || !this.draft) { return; }
    this.saving = true;
    this.confirmingPublish = true;
    this.subs.push(this.alerts.confirm('Publish these settings?',
      `Use these settings starting ${this.draft.effectiveStart}? Only metrics marked "Use this metric" can add to the score. Past results stay the same.`)
      .pipe(take(1)).subscribe(confirmed => {
        this.saving = false;
        this.confirmingPublish = false;
        if (confirmed) { this.save(true); }
      }));
  }

  save(publish = false) {
    if (this.saving || !this.selected || this.selected.retired || !this.draft) { return; }
    const target = this.selected;
    const submitted: KraMetricCriterion = JSON.parse(JSON.stringify({ ...target, configuration: { ...this.draft, approved: publish }, history: [] }));
    if (submitted.configuration.evaluationMethod !== 0 && submitted.configuration.evaluationMethod !== 2) { submitted.configuration.managerWeight = undefined; }
    if (!publish) { submitted.configuration.metrics.forEach(metric => metric.activated = false); }
    this.saving = true;
    this.error = '';
    this.subs.push(this.service.saveMetricCriterion(submitted).subscribe({
      next: result => {
        target.expectedVersionID = result.expectedVersionID;
        const published = target.history.find(x => x.approved);
        if (result.configuration.approved && published && !published.effectiveEnd) { published.effectiveEnd = result.configuration.effectiveStart; }
        target.configuration = result.configuration;
        target.history.unshift(result.configuration);
        this.saving = false;
        this.select(target);
        this.alerts.success(publish ? 'Settings published. Past results stay the same.' : 'Draft saved. Current published settings stay in use.');
      }, error: err => {
        this.error = typeof err.error === 'string' ? err.error : err.error?.message || err.message;
        this.saving = false;
      }
    }));
  }
}
